import { SelectCompanyRole } from "@components/role/select-company-role";
import { RoleChangeSuccessDescription } from "@components/table/members/members-column/role-column/role-change-success-description";
import { RolePartiallyChangedGmPm } from "@components/table/members/members-column/role-column/role-partially-changed-gm-pm";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import {
  AssignableUpdateCompanyMemberRole,
  MemberIdentityRoleProps,
} from "@custom-types/member-types";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useToast } from "@hooks/use-toast";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { updateMemberRoleInWorkspace } from "@store/members/members-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { DEFAULT_TEXT_FONT_SIZE } from "@styles/common-styles";
import { formatUserRoleType } from "@utils/data-display";

type Props = BaseCompanyIdProps & MemberIdentityRoleProps;

/**
 * Render and adjust member roles in company context.
 * This dropdown is showed to change the role for a single member inside a table.
 */
export function CompanyMemberRole({
  companyId,
  memberIdentityRole,
}: Props): JSX.Element {
  const { showToast } = useToast();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(currentUserSelector);

  const isCurrentUser = currentUser?.identity === memberIdentityRole.identity;
  const { canEditMember } = useHasUserValidRoleCompanyLevel();

  // For Project and Group Manager roles don't show the "No role" option since
  // changing to "No role" will have no effect.
  let allowedRoles: FaroCoreApiTypes.CompanyRole[] =
    AssignableUpdateCompanyMemberRole;
  if (
    memberIdentityRole.role === "COMPANY_MANAGER" ||
    memberIdentityRole.role === "PROJECT_MANAGER"
  ) {
    allowedRoles = AssignableUpdateCompanyMemberRole.filter(
      (role) => role !== "MEMBER"
    );

    allowedRoles.push(memberIdentityRole.role);
  }

  /**
   * Return non-editable member role if:
   * - Member is current logged user
   * - User has no permission to edit member
   */
  if (isCurrentUser || !canEditMember) {
    return (
      <span style={{ fontSize: DEFAULT_TEXT_FONT_SIZE }}>
        {formatUserRoleType(memberIdentityRole.role)}
      </span>
    );
  }

  /**
   * Shows a message to the user about the role change.
   * It could be that everything was changed successfully, or that the role was partially changed,
   * because the user was a manager or a group.
   */
  function notifySuccessRoleChange({
    newRole,
    intendedRole,
  }: {
    newRole: FaroCoreApiTypes.CompanyRole;
    intendedRole: FaroCoreApiTypes.CompanyRole;
  }): void {
    // If the new role is different than the role the user intended to change,
    // show a warning message because it changed to a different role.
    if (newRole !== intendedRole) {
      if (newRole === "COMPANY_MANAGER" || newRole === "PROJECT_MANAGER") {
        const newRoleName = formatUserRoleType(newRole);
        showToast({
          message: (
            <>
              Role Changed to <strong>{newRoleName}</strong>
            </>
          ),
          description: (
            <RolePartiallyChangedGmPm
              intendedRole={formatUserRoleType(intendedRole)}
              newRole={newRoleName}
            />
          ),
          type: "warning",
        });
      }
    } else {
      // Otherwise it means that the role was successfully changed to the intended one.
      showToast({
        message: "Role Changed",
        description: (
          <RoleChangeSuccessDescription role={formatUserRoleType(newRole)} />
        ),
        type: "success",
      });
    }
  }

  /** Trigger updating member role and show success message */
  async function onRoleChange(
    role: FaroCoreApiTypes.AssignmentRoleBySubjectType["company"]
  ): Promise<void> {
    try {
      const result = await dispatch(
        updateMemberRoleInWorkspace({
          companyId,
          role: role as FaroCoreApiTypes.UpdateCompanyMemberRole,
          identity: memberIdentityRole.identity,
        })
      ).unwrap();
      notifySuccessRoleChange({
        newRole: result.role,
        intendedRole: role,
      });
    } catch {
      // Errors are already handled by the slice, only notify about a successful result
      return;
    }
  }

  return (
    <SelectCompanyRole
      selectedRole={
        memberIdentityRole.role as FaroCoreApiTypes.AssignmentRoleBySubjectType["company"]
      }
      onChange={onRoleChange}
      isTableCell={true}
    />
  );
}
